import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import SucessToast from "../ui/SucessToast";
import RoleBasedLayout from "./RoleBasedLayout";
import {
  useEmployees, useUser, useCreateProject, useProjects, useUpdateProject, useDeleteTask, useRenameProjectStatus, useLoginDetials,
  useDragDropTask, useAddProjectStatus, useDeleteStatus, useGetPermissions, useAddComment, useTotalStaff, useComments
} from "../Use-auth";
import {
  Calendar, Plus, CircleCheck, Flag, CircleStop, Users, X, SendHorizontal,
  CircleDot, ClockFading, MoveLeft, Eye, Pencil, Info,
} from "lucide-react";
import { PointerSensor, useSensor, useSensors, } from "@dnd-kit/core";
import SmartDatePicker from "../ui/SmartDatePicker";
import TaskPriority from "../ui/TaskPriority";
import TaskEmployees from "../ui/TaskEmployees";
import ProjectList from "../ui/ProjectList";
import CommentsSection from "../ui/CommentsSection ";
import Input from "../ui/Input";
import TaskDetails from "../ui/TaskDetails ";
import TaskTimeline from "../ui/TaskTimeline ";
import BulkActionsBar from "../ui/BulkActionsBar ";
import TaskDescriptionPopup from "../ui/TaskDescriptionPopup ";
import TeamDetialis from "../ui/TeamProject";
import ProjectTab from "../ui/ProjectTab";
import ProjectCalender from "../ui/ProjectCalender";
import SkeletonPage from "../ui/SkeletonPage ";
import AddProject from "../ui/AddProject";
import SelectedTask from "../ui/SelectedTask";
import MyTask from "../ui/MyTask";
import PermissionPopup from "../ui/PermissionPopup";
import { getStatusColor } from "../Pages/utils/taskUtils";
import { useBulkTaskActions } from "../ui/Projects/useBulkTaskActions";
import { encryptData, decryptData } from "../../lib/crypto";
import { useProjectRealtime } from "../ui/Projects/useProjectRealtime";

const formatMySQLDate = (date) => {
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = "00";

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export default function Projects() {
  const { data: currentUser } = useUser();
  const user = currentUser;
  const { data: employees = [], error } = useEmployees();
  const createProject = useCreateProject();
  const { data: projects = [], isLoading, refetch } = useProjects();
  useProjectRealtime(user?.userId);
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteTask();
  const dragDropTaskMutation = useDragDropTask();
  const renameStatusMutation = useRenameProjectStatus();
  const addStatusMutation = useAddProjectStatus();
  const deleteStatusMutation = useDeleteStatus();
  const { data: existingPermissions, } = useGetPermissions();
  const addComment = useAddComment();
  const [statuses, setStatuses] = useState([]);
  const [newStatusName, setNewStatusName] = useState("");
  // State management
  const initStatusObject = (value) =>
    Object.fromEntries(statuses.map((s) => [s, value]));
  const [editingStatus, setEditingStatus] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState(() => initStatusObject([]));
  const [projectName, setProjectName] = useState("");
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [runningTaskId, setRunningTaskId] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showAddProjectInput, setShowAddProjectInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({
    title: "",
    description: [
      {
        storeLink: "",
        referenceLink: "",
        referenceLinkEnabled: false,
        figmaLink: "",
        figmaLinkDisabled: false,
        taskdescription: [],
        subdescription: "",
        files: [],
      },
    ],
    priority: "",
    status: "",
    assignedEmployees: [],
    dueDate: null
  });

  const [collapsedStatuses, setCollapsedStatuses] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(null);
  const [bulkDueDate, setBulkDueDate] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentEmployees, setCommentEmployees] = useState([]);
  const [openMenuStatus, setOpenMenuStatus] = useState(null);
  const [active, setActive] = useState("mytasks");
  const [activeSubProjectId, setActiveSubProjectId] = useState(null);

  // Refs
  const addTaskRef = useRef(null);
  const statusRef = useRef(null);
  const textareaRef = useRef(null);
  const menuRef = useRef(null);
  const newStatusWrapperRef = useRef(null);

  // Project order state with persistence
  const [projectOrder] = useState(() => {
    try {
      const stored = localStorage.getItem("projectsOrder");
      const parsed = stored ? decryptData(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });

  // Task order state with persistence
  const [taskOrder, setTaskOrder] = useState(() => {
    const initialOrder = {};

    statuses.forEach((s) => {
      initialOrder[s] = [];
    });

    return initialOrder;
  });

  const isAdmin = currentUser?.department === "Admin";
  const currentUserPermissions = isAdmin
    ? {
      management: {
        manager_view: true,
        manager_time: true,
      },
    }
    : existingPermissions?.find(p => p.userId === currentUser?.userId);

  const canViewHome =
    isAdmin || currentUserPermissions?.management?.project_view === true;

  const canViewManagerNewProject =
    isAdmin || currentUserPermissions?.management?.project_new === true;

  const [isBelow1280, setIsBelow1280] = useState(window.innerWidth < 1280);

  useEffect(() => {
    const handleResize = () => {
      setIsBelow1280(window.innerWidth < 1280);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (active === "team") {
      setSelectedTasks({});
    }
    if (active === "calendar") {
      setSelectedTasks({});
    }
    if (active === "mytasks") {
      setSelectedTasks({});
    }
  }, [active]);

  useEffect(() => {
    setSelectedTasks({});
  }, [activeProjectId, activeSubProjectId]);

  useEffect(() => {
    const encrypted = encryptData(projectOrder);
    localStorage.setItem("projectsOrder", encrypted);
  }, [projectOrder]);

  const handleInput = (e) => {
    setEditData((d) => ({ ...d, title: e.target.value }));
  };

  useLayoutEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [editData.title]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, []);

  // hide Outside click //
  useEffect(() => {
    function handleClickOutside(event) {
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
      if (addTaskRef.current && !addTaskRef.current.contains(event.target)) {
        setEditingStatus(null);
      }

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuStatus(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeProject = useMemo(
    () => projects.find((p) => String(p._id || p.id) === String(activeProjectId)),
    [projects, activeProjectId]
  );

  const activeSubProjectObj = useMemo(() => {
    if (!activeProject?.isMultiProject) return null;
    if (!activeSubProjectId) return null;

    return (
      activeProject.subProjects?.find(
        (sp) => String(sp.id) === String(activeSubProjectId)
      ) || null
    );
  }, [activeProject, activeSubProjectId]);

  const selectedProjectData = activeSubProjectObj || activeProject;

  useEffect(() => {
    if (!activeProject) {
      setStatuses([]);
      return;
    }

    if (activeProject.isMultiProject) {
      if (!activeSubProjectObj) {
        setStatuses([]);
        return;
      }
      setStatuses(activeSubProjectObj.statusOrder || []);
    } else {
      setStatuses([...new Set(activeProject.statusOrder || [])]);
    }
  }, [activeProject, activeSubProjectObj]);

  useEffect(() => {
    if (
      activeProject?.isMultiProject &&
      activeProject.subProjects?.length &&
      !activeSubProjectId
    ) {
    }
  }, [activeProject, activeSubProjectId]);

  const addStatus = () => {
    const trimmed = newStatusName.trim().toLowerCase();
    if (!trimmed) return;
    if (!activeProjectId) return;
    if (addStatusMutation.isLoading) return;

    if (activeProject?.isMultiProject && !activeSubProjectId) {
      setToastMessage("Please select a sub project first");
      setToastType("error");
      return;
    }

    if (statuses.includes(trimmed)) {
      setNewStatusName("");
      return;
    }

    const payload = {
      projectId: activeProjectId,
      status: trimmed,
    };

    if (activeProject?.isMultiProject) {
      payload.subProjectId = activeSubProjectId;
    }

    addStatusMutation.mutate(payload, {
      onSuccess: () => {
        setNewStatusName("");
      },
      onError: (err) => {
        setToastMessage(err.message || "Failed to add status");
        setToastType("error");
      },
    });
  };

  const handleClickOutside = useCallback(
    (e) => {
      if (
        !newStatusWrapperRef.current ||
        newStatusWrapperRef.current.contains(e.target)
      ) return;

      addStatus();
    },
    [addStatus]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);


  const allSelectedTasks = useMemo(() => {
    const ids = Object.values(selectedTasks).flat();
    return ids;
  }, [selectedTasks]);

  const selectedTaskObjects = useMemo(() => {
    if (!allSelectedTasks.length) {
      return [];
    }

    const selectedSet = new Set(allSelectedTasks);
    const result = [];

    for (const project of projects) {

      if (project.isMultiProject) {

        for (const sp of project.subProjects || []) {

          for (const tasks of Object.values(sp.statusTask || {})) {

            for (const task of tasks) {

              if (selectedSet.has(task._id)) {

                result.push({
                  ...task,
                  projectId: project._id,
                  subProjectId: sp._id,
                  projectName: project.projectName,
                  subProjectName: sp.projectName,
                });

              }

            }

          }

        }

      } else {

        for (const tasks of Object.values(project.statusTask || {})) {

          for (const task of tasks) {

            if (selectedSet.has(task._id)) {
              result.push({
                ...task,
                projectId: project._id,
                projectName: project.projectName,
              });

            }

          }

        }

      }

    }
    return result;

  }, [projects, allSelectedTasks]);

  const commonAssignees = useMemo(
    () =>
      selectedTaskObjects.length
        ? selectedTaskObjects
          .map((t) => t.assignedEmployees?.map((e) => e.userId) || [])
          .reduce((a, b) => a.filter((id) => b.includes(id)))
        : [],
    [selectedTaskObjects]
  );

  const commonPriority = useMemo(() => {
    if (!selectedTaskObjects.length) return null;
    const priorities = selectedTaskObjects.map((t) => t.priority || null);
    const first = priorities[0];
    const allSame = priorities.every((p) => p === first);

    return allSame ? first : null;
  }, [selectedTaskObjects]);

  const nowIST = new Date().toLocaleString("sv-SE", {
    timeZone: "Asia/Kolkata",
  }).replace(" ", "T");

  const normalizeTaskAssign = (value) => {
    try {
      if (Array.isArray(value)) return value;

      if (typeof value === "string") {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) return parsed;

        if (typeof parsed === "string") return [parsed];
      }

      return [];
    } catch (e) {
      return [];
    }
  };

  const normalizeDescFull = (desc = []) => {
    const cleaned = desc.map((d) => ({
      storeLink: d.storeLink || "",
      referenceLink: d.referenceLink || "",
      figmaLink: d.figmaLink || "",
      referenceLinkDisabled: !!d.referenceLinkDisabled,
      figmaLinkDisabled: !!d.figmaLinkDisabled,
      subdescription: d.subdescription || "",

      taskdescription: (d.taskdescription || []).map((t) => ({
        description: t.description || "",
        taskAssign: normalizeTaskAssign(t.taskAssign)
          .map((e) =>
            typeof e === "object" ? e._id || e.employeeId : e
          )
          .filter(Boolean)
          .sort()
          .join(","),
        taskDate: t.taskDate || "",
        taskStatus: t.taskStatus || "",
      })),

      files: (d.files || []).map((f) => ({
        name: f.name || "",
        url: f.url || "",
        type: f.type || "",
      })),
    }));

    const filtered = cleaned.filter((d) => {
      return (
        d.storeLink ||
        d.referenceLink ||
        d.figmaLink ||
        d.subdescription ||
        d.taskdescription.length > 0 ||
        d.files.length > 0
      );
    });

    return filtered;
  };


  const generateComments = useCallback((taskToUpdate, editDataParam, user) => {
    const comments = [];

    const userName =
      user?.fullName || user?.firstName || user?.username || "Unknown";

    const normalizeArray = (arr) =>
      (arr || [])
        .map((e) => {
          if (!e) return "";
          if (typeof e === "object") {
            return String(
              e._id ||
              e.employeeId ||
              e.userId ||
              ""
            );
          }

          return String(e);
        })
        .filter(Boolean)
        .sort();

    const normalizeDate = (date) => {
      if (!date) return null;
      return new Date(date).toISOString().split("T")[0];
    };

    if (!taskToUpdate) {
      comments.push({
        text: `${userName} created this task`,
        createdBy: userName,
        createdAt: nowIST,
      });
      return comments;
    }

    if (taskToUpdate.title !== editDataParam.title) {
      comments.push({
        text: `${userName} updated the title to "${editDataParam.title}"`,
        createdBy: userName,
        createdAt: nowIST,
      });
    }

    const oldDesc = JSON.stringify(normalizeDescFull(taskToUpdate.description));
    const newDesc = JSON.stringify(normalizeDescFull(editDataParam.description));

    if (oldDesc !== newDesc) {
      comments.push({
        text: `${userName} updated the description`,
        createdBy: userName,
        createdAt: nowIST,
      });
    }

    if (taskToUpdate.priority !== editDataParam.priority) {
      comments.push({
        text: `${userName} set priority to ${editDataParam.priority}`,
        createdBy: userName,
        createdAt: nowIST,
      });
    }

    if ((taskToUpdate.status || "") !== (editDataParam.status || "")) {
      comments.push({
        text: `${userName} changed status to "${editDataParam.status}"`,
        createdBy: userName,
        createdAt: nowIST,
      });
    }

    const oldEmployees = normalizeArray(taskToUpdate.assignedEmployees);
    const newEmployees = normalizeArray(editDataParam.assignedEmployees);

    if (JSON.stringify(oldEmployees) !== JSON.stringify(newEmployees)) {
      comments.push({
        text: `${userName} updated assignees`,
        createdBy: userName,
        createdAt: nowIST,
      });
    }

    const oldDue = normalizeDate(taskToUpdate.dueDate);
    const newDue = normalizeDate(editDataParam.dueDate);

    if (oldDue !== newDue) {
      comments.push({
        text: `${userName} set the due date to ${newDue ? new Date(newDue).toLocaleDateString() : "none"
          }`,
        createdBy: userName,
        createdAt: nowIST,
      });
    }

    return comments;
  }, []);

  const findSubProjectIdByTask = (projects, taskId) => {
    for (const project of projects || []) {
      if (!project.subProjects) continue;

      for (const sub of project.subProjects) {
        const statusTask = sub.statusTask || {};

        for (const tasks of Object.values(statusTask)) {
          if (!Array.isArray(tasks)) continue;

          if (tasks.some((t) => t._id === taskId)) {
            return sub._id;
          }
        }
      }
    }
    return null;
  };

  const handleClosePopup = useCallback(() => {
    if (updateProject.isLoading) return;

    if (!currentTask?._id || !currentTask?.projectId) {
      setShowDescriptionPopup(false);
      return;
    }

    const normalizeDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      return isNaN(d) ? null : d.toISOString().split("T")[0];
    };

    const normalizeEmployees = (arr) =>
      (arr || [])
        .map((e) => {
          if (!e) return "";
          if (typeof e === "object")
            return String(e._id || e.employeeId || "");
          return String(e);
        })
        .filter(Boolean)
        .sort()
        .join(",");

    const cleanTask = (task) => ({
      title: (task.title || "").trim(),
      status: task.status || "",
      priority: task.priority || "",
      dueDate: normalizeDate(task.dueDate),
      assignedEmployees: normalizeEmployees(task.assignedEmployees),
      description: JSON.stringify(normalizeDescFull(task.description)),
    });

    const original = cleanTask(currentTask);
    const edited = cleanTask(editData);


    const isChanged =
      JSON.stringify(original) !== JSON.stringify(edited);

    const newComments = generateComments(
      currentTask,
      editData,
      currentUser
    );

    if (!isChanged && newComments.length === 0) {
      setShowDescriptionPopup(false);
      return;
    }

    const derivedSubProjectId =
      currentTask.subProjectId ||
      findSubProjectIdByTask(projects, currentTask._id);

    const payload = {
      id: currentTask.projectId,
      subProjectId: derivedSubProjectId,
      taskId: currentTask._id,
      taskTitle: editData.title,
      description: normalizeDescFull(editData.description),
      priority: editData.priority,
      status: editData.status,
      dueDate: editData.dueDate
        ? formatMySQLDate(editData.dueDate)
        : null,
      assignedEmployees: editData.assignedEmployees || [],
      comments: newComments || [],
    };

    setIsSaving(true);

    updateProject.mutate(payload, {
      onSuccess: () => {
        setIsSaving(false);
        setShowDescriptionPopup(false);
        setToastMessage("Task updated successfully!");
        setToastType("success");
      },
      onError: () => {
        setIsSaving(false);
        setToastMessage("Error updating task");
        setToastType("error");
      },
    });
  }, [currentTask, editData, currentUser, projects]);

  // choose task  in checkbox//
  const toggleTaskSelection = useCallback((status, taskId) => {
    setSelectedTasks((prev) => {
      const current = prev[status] || [];
      const updatedStatus = current.includes(taskId)
        ? current.filter((id) => id !== taskId)
        : [...current, taskId];

      return { ...prev, [status]: updatedStatus };
    });
  }, []);

  const handleAddComment = useCallback(() => {
    if (!commentText.trim()) {
      console.log("No comment text provided");
      console.groupEnd();
      return;
    }

    const currentUserName =
      currentUser?.fullName ||
      currentUser?.firstName ||
      currentUser?.username ||
      "Unknown";

    const assignedText = commentEmployees.length
      ? `${currentUserName} set comment: "${commentText}" assigned to: ${commentEmployees
        .map((id) => {
          const employee = employees.find(
            (e) =>
              String(e.employeeId).toLowerCase().trim() ===
              String(id).toLowerCase().trim() ||
              `${e.firstName} ${e.lastName}`.toLowerCase().trim() ===
              String(id).toLowerCase().trim()
          );

          if (!employee) return id;

          const first = employee.firstName?.trim() || "";
          const last = employee.lastName?.trim() || "";

          return `${first} ${last}`.trim();
        })
        .join(", ")}`
      : `${currentUserName} commented: "${commentText}"`;

    const newComment = {
      text: assignedText,
      createdByuserId: currentUser.userId,
      createdBy: currentUserName,
      createdAt: nowIST,
    };

    setEditData((prev) => ({
      ...prev,
      comments: prev.comments ? [...prev.comments, newComment] : [newComment],
    }));

    setCommentText("");
    setCommentEmployees([]);

    if (!currentTask?._id || !currentTask?.projectId) {
      console.groupEnd();
      return;
    }

    const derivedSubProjectId =
      currentTask.subProjectId || findSubProjectIdByTask(projects, currentTask._id);

    const payload = {
      projectId: currentTask.projectId,
      taskId: currentTask._id,
      data: {
        subProjectId: derivedSubProjectId,
        comments: [newComment],
      },
    };

    addComment.mutate(payload, {
      onSuccess: (res) => {
        setToastMessage("Comment added successfully!");
        setToastType("success");

        console.groupEnd();
      },
      onError: (err) => {
        setToastMessage("Error adding comment");
        setToastType("error");
        console.groupEnd();
      },
    });
  }, [
    commentText,
    commentEmployees,
    currentTask,
    currentUser,
    employees,
    projects,
    addComment,
    findSubProjectIdByTask,
  ]);

  useEffect(() => {
    if (!allSelectedTasks.length) {
      setBulkDueDate(null);
      return;
    }

    const relevantTasks = projects.flatMap((project) =>
      project.isMultiProject
        ? project.subProjects?.flatMap((sp) =>
          Object.values(sp.statusTask || {}).flat()
        )
        : Object.values(project.statusTask || {}).flat()
    );

    const selectedTasksDates = relevantTasks
      .filter((t) => allSelectedTasks.includes(t._id))
      .map((t) => t.dueDate)
      .filter(Boolean);

    if (!selectedTasksDates.length) {
      setBulkDueDate(null);
      return;
    }

    const normalizedDates = selectedTasksDates.map(
      (d) => new Date(d).toISOString()
    );

    const allSame = normalizedDates.every(
      (d) => d === normalizedDates[0]
    );

    const newBulkDate = allSame
      ? new Date(normalizedDates[0])
      : null;

    setBulkDueDate(newBulkDate);
  }, [allSelectedTasks, projects]);

  const localProjects = useMemo(() => {
    if (!projects || !projects.length) return [];

    const projectsMap = Object.fromEntries(
      projects.map((p) => [p._id, p])
    );

    const orderedProjects = (projectOrder || [])
      .map((id) => projectsMap[id])
      .filter(Boolean);

    const newProjects = projects.filter((p) => !projectOrder.includes(p._id));

    return [...orderedProjects, ...newProjects];
  }, [projects, projectOrder]);

  useEffect(() => {
    statuses.forEach((status) => {
      const tasks = localProjects.flatMap(p => {
        if (p.isMultiProject) {
          return p.subProjects?.flatMap(sp =>
            Object.entries(p.statusTask || {})
              .flatMap(([key, arr]) =>
                key === status && Array.isArray(arr)
                  ? arr.map(t => t._id)
                  : []
              )
          ) || [];
        }

        return Object.entries(p.statusTask || {})
          .flatMap(([key, arr]) =>
            key === status ? arr.map(t => t._id) : []
          );
      });

      setTaskOrder((prev) => {
        const missingTasks = tasks.filter((t) => !prev[status]?.includes(t));
        if (missingTasks.length === 0) return prev;
        const updated = {
          ...prev,
          [status]: [...(prev[status] || []), ...missingTasks],
        };
        return updated;
      });
    });
  }, [statuses, localProjects]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const toggleStatus = (status) => {
    setCollapsedStatuses((prev) => ({
      ...prev,
      [status]: prev[status] === undefined ? true : !prev[status],
    }));
  };

  const tasksByStatus = useMemo(() => {
    const map = {};
    const activeProject = projects.find(
      (p) => String(p._id || p.id) === String(activeProjectId)
    );
    if (!activeProject) return map;

    let statusTaskSource = null;

    if (activeProject.isMultiProject) {
      if (!activeSubProjectId) return map;

      const subProject = activeProject.subProjects?.find(
        (sp) => String(sp._id) === String(activeSubProjectId)
      );

      if (!subProject || !subProject.statusTask) return map;

      statusTaskSource = subProject.statusTask;
    } else {
      statusTaskSource = activeProject.statusTask;
    }

    Object.entries(statusTaskSource || {}).forEach(([status, tasks]) => {
      map[status] = (tasks || []).filter((task) => {
        if (activeProject.isMultiProject) {
          return String(task.subProjectId) === String(activeSubProjectId);
        }
        return true;
      });
    });

    return map;
  }, [projects, activeProjectId, activeSubProjectId]);

  const formatStatusLabel = (status = "") =>
    status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  useEffect(() => {
    if (statuses?.length) {
      const obj = {};
      statuses.forEach((s) => {
        obj[s] = false;
      });
      setCollapsedStatuses(obj);
    }
  }, [statuses]);

  const renderProjectTasks = useCallback(() => (
    <AddProject
      canViewManagerNewProject={canViewManagerNewProject}
      currentUser={currentUser}
      setShowAddProjectInput={setShowAddProjectInput}
      projectName={projectName}
      createProject={createProject}
      setProjectName={setProjectName}
      addStatusMutation={addStatusMutation}
      showAddProjectInput={showAddProjectInput}
      Input={Input}
      TaskEmployees={TaskEmployees}
      employees={employees}
      X={X}
      ProjectList={ProjectList}
      setActive={setActive}
      projects={projects}
      activeProjectId={activeProjectId}
      setActiveProjectId={setActiveProjectId}
      SucessToast={SucessToast}
      activeSubProjectId={activeSubProjectId}
      setActiveSubProjectId={setActiveSubProjectId}
      statuses={statuses}
    />
  ), [
    showAddProjectInput,
    projectName,
    projects,
    activeProjectId,
    employees,
    currentUser,
    canViewManagerNewProject,
  ]);

  const timelineArray = useMemo(() => {
    if (!editData?.timeline) return [];
    return Array.isArray(editData.timeline)
      ? editData.timeline
      : [editData.timeline];
  }, [editData?.timeline]);

  const { data: comments, } = useComments({
    projectId: activeProjectId,
    taskId: currentTask?._id,
    subProjectId: activeSubProjectObj?._id,
  });

  const mergedComments = useMemo(() => {
    if (comments && comments.length > 0) {
      return comments;
    }
    return editData.comments || [];
  }, [comments, editData.comments]);

  function handleTaskcheck(projectId, subProjectId, taskId, status) {

    setActive("list");
    setActiveProjectId(projectId);
    setActiveSubProjectId(subProjectId || null);

    setCollapsedStatuses((prev) => ({
      ...prev,
      [status]: false,
    }));

    setTimeout(() => {
      const taskEl = document.getElementById(`task-${taskId}`);
      taskEl?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }

  const {
    handleBulkPriorityUpdate,
    handleBulkAssigneeUpdate,
    handleBulkStatusUpdate,
    handleBulkDateUpdate,
    handleCopyTasks,
    handleDeleteTasks
  } = useBulkTaskActions({
    currentUser,
    employees,
    updateProject,
    setToastMessage,
    setToastType,
    activeProjectId,
    deleteProject,
    setSelectedTasks,
    setShowStatusDropdown,
    selectedTaskObjects,
    nowIST
  });

  useEffect(() => {
    if (!projects.length) return;

    const params = new URLSearchParams(window.location.search);

    const projectId = params.get("projectId");
    const subProjectId = params.get("subProjectId");
    const taskId = params.get("taskId");
    const tab = params.get("tab");

    if (tab) {
      setActive(tab);
    }

    if (projectId) {
      const exists = projects.some(
        (p) => String(p._id || p.id) === String(projectId)
      );

      if (exists) {
        setActiveProjectId(String(projectId));
      }
    }

    if (subProjectId) {
      setActiveSubProjectId(String(subProjectId));
    }

    if (taskId) {
      setTimeout(() => {
        const el = document.getElementById(`task-${taskId}`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    }

  }, [projects]);

  if (isLoading || !currentUserPermissions) {
    return (
      <RoleBasedLayout>
        <SkeletonPage />
      </RoleBasedLayout>
    );
  }

  if (!canViewHome) {
    return (
      <RoleBasedLayout>
        <div className="relative h-[90.7vh] overflow-hidden">
          <PermissionPopup />
        </div>
      </RoleBasedLayout>
    );
  }

  return (
    <RoleBasedLayout>
      <div className="relative z-1">
        <div>
          <ProjectTab
            setActive={setActive}
            active={active}
            user={currentUser}
            activeProjectId={activeProjectId}
          />

          {active !== "mytasks" &&
            (
              !activeProjectId ||
              (activeProject?.isMultiProject && !activeSubProjectId)
            ) &&
            renderProjectTasks()
          }

          {active === "mytasks" &&
            <MyTask
              projects={projects}
              user={currentUser}
              employees={employees}
              formatStatusLabel={formatStatusLabel}
              getStatusColor={getStatusColor}
              TaskEmployees={TaskEmployees}
              onTaskClick={(projectId, subProjectId, taskId, status) =>
                handleTaskcheck(projectId, subProjectId, taskId, status)
              }
              TaskPriority={TaskPriority}
            />
          }

          {active === "list" && (<div>
            <SelectedTask
              active={active}
              activeProjectId={activeProjectId}
              statuses={statuses}
              tasksByStatus={tasksByStatus}
              taskOrder={taskOrder}
              sensors={sensors}
              toggleTaskSelection={toggleTaskSelection}
              selectedTasks={selectedTasks}
              employees={employees}
              currentUser={currentUser}
              updateProject={updateProject}
              setToastMessage={setToastMessage}
              setToastType={setToastType}
              setCurrentTask={setCurrentTask}
              setRunningTaskId={setRunningTaskId}
              setEditData={setEditData}
              setShowDescriptionPopup={setShowDescriptionPopup}
              setSelectedTasks={setSelectedTasks}
              isBelow1280={isBelow1280}
              collapsedStatuses={collapsedStatuses}
              toggleStatus={toggleStatus}
              activeProject={activeProject}
              activeSubProjectObj={activeSubProjectObj}
              Plus={Plus}
              newStatusWrapperRef={newStatusWrapperRef}
              Input={Input}
              newStatusName={newStatusName}
              setNewStatusName={setNewStatusName}
              MoveLeft={MoveLeft}
              Pencil={Pencil}
              X={X}
              Info={Info}
              useLoginDetials={useLoginDetials}
              Eye={Eye}
              selectedProjectData={selectedProjectData}
              setActiveProjectId={setActiveProjectId}
              setActiveSubProjectId={setActiveSubProjectId}
              activeSubProjectId={activeSubProjectId}
              projects={projects}
              addStatus={addStatus}
              selectedTaskObjects={selectedTaskObjects}
              setTaskOrder={setTaskOrder}
              renameStatusMutation={renameStatusMutation}
              setOpenMenuStatus={setOpenMenuStatus}
              openMenuStatus={openMenuStatus}
              menuRef={menuRef}
              deleteStatusMutation={deleteStatusMutation}
              addTaskRef={addTaskRef}
              CircleCheck={CircleCheck}
              formatStatusLabel={formatStatusLabel}
              localProjects={localProjects}
              dragDropTaskMutation={dragDropTaskMutation}
              setStatuses={setStatuses}
              setShowDatePicker={setShowDatePicker}
              nowIST={nowIST}
              getStatusColor={getStatusColor}
              ProjectCalender={ProjectCalender}
              TeamDetialis={TeamDetialis}
              refetch={refetch}
            />
          </div>)}
          <TaskDescriptionPopup
            showDescriptionPopup={showDescriptionPopup}
            currentTask={currentTask}
            CircleDot={CircleDot}
            textareaRef={textareaRef}
            editData={editData}
            setEditData={setEditData}
            handleInput={handleInput}
            CircleStop={CircleStop}
            setShowDatePicker={setShowDatePicker}
            Calendar={Calendar}
            showDatePicker={showDatePicker}
            Flag={Flag}
            Users={Users}
            TaskPriority={TaskPriority}
            TaskEmployees={TaskEmployees}
            runningTaskId={runningTaskId}
            ClockFading={ClockFading}
            TaskTimeline={TaskTimeline}
            TaskDetails={TaskDetails}
            X={X}
            handleClosePopup={handleClosePopup}
            CommentsSection={CommentsSection}
            handleAddComment={handleAddComment}
            setCommentText={setCommentText}
            commentText={commentText}
            setCommentEmployees={setCommentEmployees}
            SendHorizontal={SendHorizontal}
            getStatusColor={getStatusColor}
            SmartDatePicker={SmartDatePicker}
            employees={employees}
            timelineArray={timelineArray}
            commentEmployees={commentEmployees}
            Input={Input}
            currentUser={currentUser}
            projects={projects}
            mergedComments={mergedComments}
            formatStatusLabel={formatStatusLabel}
            statuses={statuses}
            statusRef={statusRef}
            isSaving={isSaving}
          />
          <BulkActionsBar
            activeProjectId={activeProjectId}
            allSelectedTasks={allSelectedTasks}
            selectedTaskObjects={selectedTaskObjects}
            statuses={statuses}
            showStatusDropdown={showStatusDropdown}
            setShowStatusDropdown={setShowStatusDropdown}
            commonAssignees={commonAssignees}
            commonPriority={commonPriority}
            handleBulkPriorityUpdate={handleBulkPriorityUpdate}
            handleBulkAssigneeUpdate={handleBulkAssigneeUpdate}
            employees={employees}
            showDatePicker={showDatePicker}
            setShowDatePicker={setShowDatePicker}
            bulkDueDate={bulkDueDate}
            setBulkDueDate={setBulkDueDate}
            handleBulkDateUpdate={handleBulkDateUpdate}
            handleBulkStatusUpdate={handleBulkStatusUpdate}
            handleCopyTasks={handleCopyTasks}
            handleDeleteTasks={handleDeleteTasks}
            statusRef={statusRef}
            formatStatusLabel={formatStatusLabel}
            TaskPriority={TaskPriority}
          />
          {toastMessage && (
            <SucessToast type={toastType} message={toastMessage} />
          )}
        </div>
      </div>
    </RoleBasedLayout>
  );
}  